import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../api/client';
import type { SkuSugerido } from '../components/BuscadorSku';

/** Article as the purchase searches return it (GET catalogo/buscar, remitos/skus). */
export type ArticuloCompra = SkuSugerido & {
  unidadMedidaBase?: string;
  precioUltimaCompra?: number | null;
  precioVenta?: number | null;
  ean?: string | null;
};

/** Codes per exact lookup: the backend's MAX_CODIGOS_EXACTOS (catalogo/buscar-articulos.ts) answers 400 above it. */
export const MAX_CODIGOS_EXACTOS = 100;
const DEMORA_MS = 250;
const clave = (c?: string | null) => (c ?? '').trim().toLowerCase();

export function urlBuscarArticulos(
  base: string, p: { q?: string; codigos?: string[]; proveedorId?: string | number; incluirOtros?: boolean },
): string {
  const partes: string[] = [];
  if (p.q !== undefined) partes.push(`q=${encodeURIComponent(p.q)}`);
  if (p.codigos !== undefined) partes.push(`codigos=${encodeURIComponent(p.codigos.join(','))}`);
  if (p.proveedorId !== undefined && p.proveedorId !== '') partes.push(`proveedorId=${encodeURIComponent(String(p.proveedorId))}`);
  if (p.incluirOtros) partes.push('incluirOtros=true');
  return partes.length ? `${base}?${partes.join('&')}` : base;
}

/**
 * Search and exact-code resolver of a purchase document's SKU fields, limited to its supplier unless `incluirOtros`.
 * The supplier of each picked article is kept here (by SKU), never on the line: request bodies reject unknown fields.
 */
export function useBuscadorCompra(base: string, proveedorId: string | number | '', incluirOtros: boolean) {
  const [proveedores, setProveedores] = useState<Record<string, SkuSugerido['proveedor']>>({});
  const buscar = useCallback(
    (t: string) => api.get<ArticuloCompra[]>(urlBuscarArticulos(base, { q: t, proveedorId, incluirOtros })),
    [base, proveedorId, incluirOtros],
  );
  const resolver = useCallback(
    (c: string) => api.get<ArticuloCompra[]>(urlBuscarArticulos(base, { codigos: [c], proveedorId, incluirOtros })),
    [base, proveedorId, incluirOtros],
  );
  const recordar = useCallback((a: SkuSugerido) => {
    setProveedores(p => ({ ...p, [clave(a.skuInterno)]: a.proveedor ?? null }));
  }, []);
  const proveedorDe = (sku?: string): SkuSugerido['proveedor'] => proveedores[clave(sku)];
  return { buscar, resolver, recordar, proveedorDe };
}

/**
 * Exact lookup of the codes on screen (whole catalog, no supplier filter), for «this SKU does not exist» warnings and
 * reference prices: new codes are batched after a pause and asked once; `consultado` is true once the answer arrived.
 */
export function useArticulosPorCodigo(base: string, codigos: string[]) {
  // By skuInterno; by codigo apart, so that a shared code (IBICO A10001 with codigo 400007) never shadows another SKU.
  const porSku = useRef(new Map<string, ArticuloCompra>());
  const porCodigo = useRef(new Map<string, ArticuloCompra>());
  const consultados = useRef(new Set<string>());
  const pedidos = useRef(new Set<string>());
  const [, setVersion] = useState(0);
  const firma = codigos.map(c => (c ?? '').trim()).join('\n');

  useEffect(() => {
    const nuevos = [...new Map(firma.split('\n').filter(c => c && !pedidos.current.has(clave(c))).map(c => [clave(c), c])).values()];
    if (nuevos.length === 0) return;
    const t = setTimeout(() => {
      for (let i = 0; i < nuevos.length; i += MAX_CODIGOS_EXACTOS) {
        const lote = nuevos.slice(i, i + MAX_CODIGOS_EXACTOS);
        lote.forEach(c => pedidos.current.add(clave(c)));
        api.get<ArticuloCompra[]>(urlBuscarArticulos(base, { codigos: lote })).then(r => {
          for (const a of Array.isArray(r) ? r : []) {
            porSku.current.set(clave(a.skuInterno), a);
            if (a.codigo && !porCodigo.current.has(clave(a.codigo))) porCodigo.current.set(clave(a.codigo), a);
          }
          lote.forEach(c => consultados.current.add(clave(c)));
          setVersion(v => v + 1);
        }, () => { lote.forEach(c => pedidos.current.delete(clave(c))); });
      }
    }, DEMORA_MS);
    return () => clearTimeout(t);
  }, [base, firma]);

  return {
    consultado: (c?: string) => !!clave(c) && consultados.current.has(clave(c)),
    articulo: (c?: string) => porSku.current.get(clave(c)) ?? porCodigo.current.get(clave(c)),
  };
}

type FilaListaProveedor = {
  skuInterno?: string | null; codigoProveedor?: string | null; descripcion?: string | null; activo?: boolean;
  precioUnitario?: unknown; catalogo?: { descripcion?: string | null };
};

/**
 * Options of a Cod.Prov field from the supplier's price list (already loaded): rows matching code or description; only
 * active rows unless `soloActivos` is false (invoices still offer inactive ones, marked).
 */
export function opcionesCodProv(
  articulos: FilaListaProveedor[], texto: string, proveedor: { id: number; nombre: string } | null, limite = 12, soloActivos = true,
): SkuSugerido[] {
  const q = clave(texto);
  return articulos
    .filter(a => !soloActivos || a.activo !== false)
    .filter(a => clave(a.codigoProveedor).includes(q) || clave(a.descripcion ?? a.catalogo?.descripcion).includes(q))
    .slice(0, limite)
    .map(a => ({
      skuInterno: a.skuInterno ?? '',
      codigo: a.skuInterno ?? 'sin SKU',
      descripcion: a.descripcion || a.catalogo?.descripcion || null,
      proveedor,
      codigoProveedor: a.codigoProveedor,
      ...(a.activo === false && { activo: false }),
    }));
}
